<script setup lang="ts">
import { BaseEdge, EdgeLabelRenderer, type EdgeProps } from '@vue-flow/core'
import { ZhTooltip } from '@zerohuman/ui'
import { computed } from 'vue'
import { TASK_GRAPH_COLUMN_GAP, type TaskGraphEdge } from '@/lib/taskGraph'

type Point = { x: number; y: number }
type Obstacle = Point & { width: number; height: number }
const props =
    defineProps<
        EdgeProps<
            TaskGraphEdge & {
                showLabel: boolean
                routeY: number
                muted?: boolean
                active?: boolean
                obstacles?: Obstacle[]
            }
        >
    >()
const label = computed(() =>
    [props.data?.on ?? 'succeeded', props.data?.when ? `when ${props.data.when}` : null].filter(Boolean).join(' · '),
)
function roundedPath(points: Point[]): string {
    const first = points[0]
    if (!first) return ''
    let path = `M ${first.x},${first.y}`
    for (let i = 1; i < points.length - 1; i++) {
        const previous = points[i - 1]
        const current = points[i]
        const next = points[i + 1]
        if (!previous || !current || !next) continue
        const before = Math.hypot(current.x - previous.x, current.y - previous.y)
        const after = Math.hypot(next.x - current.x, next.y - current.y)
        const radius = Math.min(16, before / 2, after / 2)
        if (before === 0 || after === 0) continue
        const start = {
            x: current.x + ((previous.x - current.x) * radius) / before,
            y: current.y + ((previous.y - current.y) * radius) / before,
        }
        const end = {
            x: current.x + ((next.x - current.x) * radius) / after,
            y: current.y + ((next.y - current.y) * radius) / after,
        }
        path += ` L ${start.x},${start.y} Q ${current.x},${current.y} ${end.x},${end.y}`
    }
    const last = points[points.length - 1] ?? first
    return `${path} L ${last.x},${last.y}`
}

function labelPosition(points: Point[]): Point {
    const segments = points.slice(1).map((end, i) => ({ start: points[i] ?? end, end }))
    segments.sort(
        (a, b) =>
            Math.hypot(b.end.x - b.start.x, b.end.y - b.start.y) - Math.hypot(a.end.x - a.start.x, a.end.y - a.start.y),
    )
    const width = Math.min(172, label.value.length * 5.5 + 14)
    const height = Math.ceil((label.value.length * 5.5 + 14) / 172) * 13 + 8
    const clear = (point: Point) =>
        !(props.data?.obstacles ?? []).some(
            node =>
                point.x + width / 2 > node.x - 8 &&
                point.x - width / 2 < node.x + node.width + 8 &&
                point.y + height / 2 > node.y - 8 &&
                point.y - height / 2 < node.y + node.height + 8,
        )
    for (const segment of segments) {
        for (const fraction of [0.5, 0.3, 0.7]) {
            const point = {
                x: segment.start.x + (segment.end.x - segment.start.x) * fraction,
                y: segment.start.y + (segment.end.y - segment.start.y) * fraction,
            }
            if (clear(point)) return point
        }
    }
    return { x: props.sourceX + TASK_GRAPH_COLUMN_GAP / 2, y: props.sourceY }
}

const route = computed(() => {
    const { sourceX: sx, sourceY: sy, targetX: tx, targetY: ty } = props
    const lane = ((props.data?.parallelIndex ?? 0) - ((props.data?.parallelCount ?? 1) - 1) / 2) * 44
    if (tx <= sx) {
        const y = Math.max(props.data?.routeY ?? 0, sy + 100, ty + 100)
        const reach = 32 + (props.data?.parallelIndex ?? 0) * 12
        return {
            path: `M ${sx},${sy} H ${sx + reach - 12} Q ${sx + reach},${sy} ${sx + reach},${sy + 12} V ${y - 12} Q ${sx + reach},${y} ${sx + reach - 12},${y} H ${tx - reach + 12} Q ${tx - reach},${y} ${tx - reach},${y - 12} V ${ty + 12} Q ${tx - reach},${ty} ${tx - reach + 12},${ty} H ${tx}`,
            x: (sx + tx) / 2,
            y,
        }
    }
    // Keep the layout's waypoints when a connection skips columns: a direct
    // Bezier can otherwise run through unrelated tasks between its endpoints.
    if (tx - sx > TASK_GRAPH_COLUMN_GAP + 1 && props.data?.points && props.data.points.length > 0) {
        const offset = ((props.data.parallelIndex ?? 0) - ((props.data.parallelCount ?? 1) - 1) / 2) * 14
        const points = [
            { x: sx, y: sy },
            ...props.data.points.slice(1, -1).map(point => ({ x: point.x, y: point.y + offset })),
            { x: tx, y: ty },
        ]
        return { path: roundedPath(points), ...labelPosition(points) }
    }
    const dx = Math.max(48, (tx - sx) / 2)
    return {
        path: `M ${sx},${sy} C ${sx + dx},${sy + lane} ${tx - dx},${ty + lane} ${tx},${ty}`,
        x: (sx + tx) / 2,
        y: (sy + ty) / 2 + lane * 0.75,
    }
})
</script>

<template>
	<BaseEdge :id="id" :path="route.path" :marker-end="markerEnd" :style="style" :interaction-width="24" />
	<EdgeLabelRenderer v-if="data?.showLabel">
		<ZhTooltip :text="label"><div class="task-edge-label nodrag nopan" :class="{ 'task-edge-label-external': data?.dangling, 'task-edge-label-muted': data?.muted, 'task-edge-label-active': data?.active }" :style="{ transform: `translate(-50%, -50%) translate(${route.x}px, ${route.y}px)` }">{{ label }}</div></ZhTooltip>
	</EdgeLabelRenderer>
</template>

<style scoped>
.task-edge-label {
    position: absolute;
    max-width: 172px;
    padding: 3px 7px;
    border: 1px solid var(--border);
    border-radius: 0;
    background: var(--card);
    color: var(--muted-foreground);
    font-size: 10px;
    line-height: 13px;
    text-align: center;
    overflow-wrap: anywhere;
    pointer-events: all;
}
.task-edge-label-muted {
    opacity: .18;
}
.task-edge-label-active {
    border-color: var(--electric-cyan);
    color: var(--electric-cyan);
}
.task-edge-label-external {
    border-style: dashed;
}
</style>
